import { client } from '../core/client';
import {
  PaymentServiceResponse,
  DeliveryPostOfficesByIndex,
  DictionaryAuthority,
  DictionaryDeliveryWarehouses,
  DictionaryColdSteelType,
  DictionaryCountry,
  DictionaryDeliveryPostOfficesBySettlement,
  PurchaseItem,
  DictionaryStreet,
  DictionaryWeaponCategory,
  DictionaryPermitType,
} from '../types';

import type { GeneralDictionaryType, DataResponse, IndividualStatus } from '../types';

export const dictionary = {
  getColdSteelType: (): Promise<DataResponse<DictionaryColdSteelType[]>> => {
    return client.get(`/referenceBook/erz/findColdSteelType`);
  },
  getColdSteelFactory: (): Promise<DataResponse<GeneralDictionaryType[]>> => {
    return client.get(`/referenceBook/erz/findFactory`);
  },
  getCountry: (): Promise<DataResponse<DictionaryCountry[]>> => {
    return client.get(`/referenceBook/address/country`);
  },
  getDistrict: (regionId: string): Promise<DataResponse<GeneralDictionaryType[]>> => {
    return client.get(`/referenceBook/address/district?regionId=${regionId}`);
  },
  getRegion: (): Promise<DataResponse<GeneralDictionaryType[]>> => {
    return client.get(`/referenceBook/address/region`);
  },
  getCity: (districtId: string): Promise<DataResponse<GeneralDictionaryType[]>> => {
    return client.get(`/referenceBook/address/settlement?districtId=${districtId}`);
  },
  getStreet: (settlementId: string): Promise<DataResponse<DictionaryStreet[]>> => {
    return client.get(`/referenceBook/address/street?settlementId=${settlementId}`);
  },
  getAuthorities: (): Promise<DataResponse<DictionaryAuthority[]>> => {
    return client.get('/referenceBook/erz/findAuthority');
  },
  getIndividualStatus: (objectOfPurchase?: string): Promise<DataResponse<IndividualStatus[]>> => {
    return client.get(
      objectOfPurchase
        ? `/referenceBook/erz/findIndividualStatus?purchase_item_type=${objectOfPurchase}`
        : '/referenceBook/erz/findIndividualStatus',
    );
  },
  getPurchaseItems: (): Promise<DataResponse<PurchaseItem[]>> => {
    return client.get(`/referenceBook/erz/findPurchaseItemIndividual`);
  },
  getWeaponCategory: (): Promise<DataResponse<DictionaryWeaponCategory[]>> => {
    return client.get('/referenceBook/erz/findFirearmCategory');
  },
  getProposal: (): Promise<DataResponse<GeneralDictionaryType[]>> => {
    return client.get('/referenceBook/iac/getProposal');
  },
  getPermitTypes: async (): Promise<DataResponse<DictionaryPermitType[]>> => {
    const response = await client.get<DataResponse<DictionaryPermitType[] | Record<string, DictionaryPermitType>>>(
      `/referenceBook/erz/getPermitsTypes`,
    );
    const permitTypes = Array.isArray(response.data) ? response.data : Object.values(response.data);
    return {
      ...response,
      data: permitTypes,
    };
  },
  getServicesForPayment: (): Promise<DataResponse<PaymentServiceResponse>> => {
    return client.get('/defrayment-services');
  },
  getDeliveryServices: (): Promise<DataResponse<GeneralDictionaryType[]>> => {
    return client.get('/referenceBook/mial/delivery-services');
  },
  getDeliveryRegions: (): Promise<DataResponse<GeneralDictionaryType[]>> => {
    return client.get('/referenceBook/mial/regions');
  },
  getDeliveryDistricts: (regionId: string): Promise<DataResponse<GeneralDictionaryType[]>> => {
    return client.get(`/referenceBook/mial/districts/${regionId}`);
  },
  getDeliverySettlements: (districtId: string): Promise<DataResponse<GeneralDictionaryType[]>> => {
    return client.get(`/referenceBook/mial/settlements/${districtId}`);
  },
  getDeliveryStreets: (settlementId: string): Promise<DataResponse<GeneralDictionaryType[]>> => {
    return client.get(`/referenceBook/mial/streets/${settlementId}`);
  },
  getDeliveryIndexStreets: (zipCode: string): Promise<DataResponse<GeneralDictionaryType[]>> => {
    return client.get(`/referenceBook/mial/index-streets/${zipCode}`);
  },
  getDeliveryPostOfficeByIndex: (zipCode: string): Promise<DataResponse<DeliveryPostOfficesByIndex[]>> => {
    return client.get(`/referenceBook/mial/post-office-addresses-by-index/${zipCode}`);
  },
  getDeliveryPostOfficeBySettlement: (
    settlementId: string,
  ): Promise<DataResponse<DictionaryDeliveryPostOfficesBySettlement[]>> => {
    return client.get(`/referenceBook/mial/post-office-addresses-by-settlement/${settlementId}`);
  },
  getDeliveryWarehouses: (settlementId: string): Promise<DataResponse<DictionaryDeliveryWarehouses[]>> => {
    return client.get(`/referenceBook/mial/houses/${settlementId}`);
  },
};
